import React, { useState } from 'react';
import { Phone, Menu, X, Home } from 'lucide-react';
import { businessInfo } from '../utils/mock';
import { Button } from './ui/button';
import { trackBookNowClick, trackPhoneClick } from '../utils/analytics';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  const scrollToSection = (sectionId) => {
    if (sectionId === 'booking') {
      trackBookNowClick('header');
    }
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-sm border-b border-red-900/20">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-24 md:h-28">
          {/* Logo/Brand - Clickable to scroll to top */}
          <div className="flex items-center space-x-3 cursor-pointer" onClick={scrollToTop}>
            <img 
              src="https://customer-assets-0z36b82j.emergentagent.net/job_mobile-detail-site/artifacts/r8awla6t_IMG_6242.jpeg"
              alt="Mobile Shine Detailing Logo"
              className="h-20 w-20 md:h-24 md:w-24 object-contain"
            />
            <div>
              <div className="text-2xl font-bold text-white hover:text-red-500 transition-colors">
                {businessInfo.name}
              </div>
              <div className="hidden md:block text-xs text-red-500 font-semibold">
                {businessInfo.tagline}
              </div>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <button
              onClick={scrollToTop}
              className="text-gray-300 hover:text-red-500 transition-colors font-medium flex items-center space-x-1"
            >
              <Home className="w-4 h-4" />
              <span>Home</span>
            </button>
            <button
              onClick={() => scrollToSection('services')}
              className="text-gray-300 hover:text-red-500 transition-colors font-medium"
            >
              Services
            </button>
            <button
              onClick={() => scrollToSection('gallery')}
              className="text-gray-300 hover:text-red-500 transition-colors font-medium"
            >
              Gallery
            </button>
            <button
              onClick={() => scrollToSection('reviews')}
              className="text-gray-300 hover:text-red-500 transition-colors font-medium"
            >
              Reviews
            </button>
            <button
              onClick={() => scrollToSection('info')}
              className="text-gray-300 hover:text-red-500 transition-colors font-medium"
            >
              Info
            </button>
            <button
              onClick={() => scrollToSection('booking')}
              className="text-gray-300 hover:text-red-500 transition-colors font-medium"
            >
              Book Now
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="text-gray-300 hover:text-red-500 transition-colors font-medium"
            >
              Contact
            </button>
            <button
              onClick={() => window.location.href = '/customer-portal'}
              className="text-gray-300 hover:text-red-500 transition-colors font-medium"
            >
              Portal
            </button>
          </nav>

          {/* Phone CTA */}
          <div className="hidden md:flex items-center space-x-4">
            <a href={`tel:${businessInfo.phone}`} className="flex items-center space-x-2 text-white hover:text-red-500 transition-colors">
              <Phone className="w-5 h-5" />
              <span className="font-semibold">{businessInfo.phone}</span>
            </a>
            <Button
              onClick={() => scrollToSection('booking')}
              className="bg-red-600 hover:bg-red-700 text-white font-semibold"
            >
              Book Now
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-white"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-red-900/20">
            <nav className="flex flex-col space-y-4">
              <button
                onClick={scrollToTop}
                className="text-gray-300 hover:text-red-500 transition-colors font-medium text-left flex items-center space-x-2"
              >
                <Home className="w-4 h-4" />
                <span>Home</span>
              </button>
              <button
                onClick={() => scrollToSection('services')}
                className="text-gray-300 hover:text-red-500 transition-colors font-medium text-left"
              >
                Services
              </button>
              <button
                onClick={() => scrollToSection('gallery')}
                className="text-gray-300 hover:text-red-500 transition-colors font-medium text-left"
              >
                Gallery
              </button>
              <button
                onClick={() => scrollToSection('reviews')}
                className="text-gray-300 hover:text-red-500 transition-colors font-medium text-left"
              >
                Reviews
              </button>
              <button
                onClick={() => scrollToSection('info')}
                className="text-gray-300 hover:text-red-500 transition-colors font-medium text-left"
              >
                Info
              </button>
              <button
                onClick={() => scrollToSection('booking')}
                className="text-gray-300 hover:text-red-500 transition-colors font-medium text-left"
              >
                Book Now
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="text-gray-300 hover:text-red-500 transition-colors font-medium text-left"
              >
                Contact
              </button>
              <button
                onClick={() => { window.location.href = '/customer-portal'; setMobileMenuOpen(false); }}
                className="text-gray-300 hover:text-red-500 transition-colors font-medium text-left"
              >
                Customer Portal
              </button>
              
              {/* Google Review Section */}
              <div className="pt-4 mt-4 border-t border-red-900/20">
                <p className="text-gray-400 text-sm mb-3 font-semibold">Check Us Out on Google</p>
                <a
                  href="https://g.page/r/CS8IeFV59cyJEBM/review"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-4 rounded-lg transition-colors text-center"
                >
                  ⭐ Leave a Google Review
                </a>
                <a
                  href="https://g.page/r/CS8IeFV59cyJEBM"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block w-full mt-2 bg-zinc-800 hover:bg-zinc-700 border-2 border-zinc-700 hover:border-red-500 text-white font-bold py-3 px-4 rounded-lg transition-colors text-center"
                >
                  🔍 View Our Google Business
                </a>
              </div>
              <a
                href={`tel:${businessInfo.phone}`}
                className="flex items-center space-x-2 text-red-500 font-semibold"
              >
                <Phone className="w-5 h-5" />
                <span>{businessInfo.phone}</span>
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
